import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { headCells, statuses } from "./fileDataTableUtils";
import FileDataTableItemMenu from "./FileDataTableItemMenu";
import { hasUserConfirmedReading, isRecallDue } from "../../../../utils/fileUtils";
import useFileData from "../../../../hooks/useFileData";

function FileDataTableRow({
  file,
  handleClick,
  isItemSelected,
  labelId,
  userData,
  isAdmin,
}) {
  const [open, setOpen] = React.useState(false);
  const { handleFileAction } = useFileData();

  const onActionClick = (event, action) => {
    event.stopPropagation();
    handleFileAction(file, action);
  };

  const recallDue = isRecallDue(file);

  return (
    <>
      <TableRow
        sx={{ "& > *": { borderBottom: "unset" }, cursor: "pointer" }}
        hover
        role="checkbox"
        aria-checked={isItemSelected}
        onClick={(event) => handleClick(event, file._id)}
        selected={isItemSelected}
      >
        {isAdmin && !file.isCheckedOut && (
          <TableCell padding="checkbox">
            <Checkbox
              color="primary"
              checked={isItemSelected}
              onClick={(event) => handleClick(event, file._id)}
              inputProps={{
                "aria-labelledby": labelId,
              }}
            />
          </TableCell>
        )}
        {file.isCheckedOut && (
          <TableCell>
            <Icon
              icon="ic:twotone-error"
              color="darkred"
              width={"2vh"}
              height={"2vh"}
            />
          </TableCell>
        )}
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? (
              <Icon icon="mdi:keyboard-arrow-up" />
            ) : (
              <Icon icon="mdi:keyboard-arrow-down" />
            )}
          </IconButton>
        </TableCell>
        {headCells.map((headCell) => {
          const { value, align } = headCell;
          if (headCell?.first) {
            return (
              <TableCell key={value} component="th" scope="row">
                {file.fileName}
              </TableCell>
            );
          }
          if (value === "status") {
            return (
              <TableCell key={value} align={align}>
                {recallDue ? (
                  <Tooltip title="Gültigkeitsprüfung erforderlich. Dokument prüfen und erneut freigeben.">
                    <Chip
                      label="PRÜFEN"
                      color="warning"
                      variant="filled"
                      sx={{
                        fontWeight: 700,
                        "@keyframes recallPulse": {
                          "0%": { opacity: 1 },
                          "50%": { opacity: 0.35 },
                          "100%": { opacity: 1 },
                        },
                        animation: "recallPulse 1.6s ease-in-out infinite",
                      }}
                    />
                  </Tooltip>
                ) : (
                  statuses[file.status]
                )}
              </TableCell>
            );
          }
          if (headCell?.type === "boolean") {
            return (
              <TableCell key={value} align={align}>
                {file[value] ? "Ja" : "Nein"}
              </TableCell>
            );
          }
          if (headCell?.value === "recallDate") {
            return (
              <TableCell key={value} align={align}>
                {file[value]
                  ? new Date(file[value]).toLocaleDateString()
                  : "Nein"}
              </TableCell>
            );
          }
          if (headCell?.type === "user") {
            return (
              <TableCell key={value} align={align}>
                {file[value] && file[value].fullName}
              </TableCell>
            );
          }
          if (headCell?.type === "date") {
            return (
              <TableCell key={value} align={align}>
                {new Date(file[value]).toLocaleString()}
              </TableCell>
            );
          }
          return (
            <TableCell key={value} align={align}>
              {file[value]}
            </TableCell>
          );
        })}
        <TableCell
          align="center"
          style={{ display: "flex", gap: "4px", alignItems: "center" }}
        >
          {file?.status === "revision" &&
            file?.assignedUser?._id === userData.id && (
              <Button
                onClick={(event) => onActionClick(event, "REVIEW")}
                variant="contained"
                color="error"
              >
                PRÜFEN
              </Button>
            )}
          {!hasUserConfirmedReading(file, userData.id) && (
            <Button
              onClick={(event) => onActionClick(event, "CONFIRMATION")}
              variant="contained"
              color="secondary"
            >
              LESEN
            </Button>
          )}
          <FileDataTableItemMenu
            fileData={file}
            userId={userData.id}
            isAdmin={isAdmin}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                Historie
              </Typography>
              <Table size="small" aria-label="file history">
                <TableHead>
                  <TableRow>
                    <TableCell>Zuletzt geändert</TableCell>
                    <TableCell>Benutzer</TableCell>
                    <TableCell>Beschreibung</TableCell>
                    <TableCell>Mitteilung</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {file?.workflow?.map((h) => (
                    <TableRow key={h.updatedAt}>
                      <TableCell>
                        {new Date(h.updatedAt).toLocaleString()}
                      </TableCell>
                      <TableCell>{h.updatedBy}</TableCell>
                      <TableCell>{h.description}</TableCell>
                      <TableCell>{h?.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

FileDataTableRow.propTypes = {
  file: PropTypes.object,
  handleEditFileData: PropTypes.func,
  handleClick: PropTypes.func,
  isItemSelected: PropTypes.bool,
  labelId: PropTypes.string,
  isAdmin: PropTypes.bool,
};

export default FileDataTableRow;
